import Component from '@glimmer/component'
import { action } from '@ember/object'
import { service } from '@ember/service'
import { trackedFunction } from 'reactiveweb/function'
import { keepLatest } from 'reactiveweb/keep-latest'
import { use } from 'ember-resources'
import type ReportingService from 'teacher-ui-client/services/reporting'
import { Product } from 'teacher-ui-client/utils/products'
import type ReportingUiScopeRoute from 'teacher-ui-client/reporting/ui-scope/route'
import type { BaseReport, BaseReportMetaPaging } from 'teacher-ui-client/services/reporting'
import { toRequestScope } from 'teacher-ui-client/services/reporting'
import { SubscriptionType } from 'teacher-ui-client/subscription-type/subscription-types'
import type RouterService from '@ember/routing/router-service'
import type SessionService from 'teacher-ui-client/services/session'
import { queryParam } from 'teacher-ui-client/decorators/query-param'
import { getSortViewModel } from 'teacher-ui-client/utils/sorting'
import { sticky } from 'teacher-ui-client/modifiers/sticky'
import { on } from '@ember/modifier'
import { fn, hash } from '@ember/helper'
import { t } from 'ember-intl'
import PhosphorIcon from 'teacher-ui-client/components/phosphor-icon'
import { BasePageSelector as PageSelector } from 'teacher-ui-client/components/base/page-selector'
import { formatValue } from 'teacher-ui-client/utils/format-value'
import { formatDuration } from 'teacher-ui-client/utils/format-duration'
import { gradeName } from 'teacher-ui-client/helpers/grade-name'
import { round } from 'teacher-ui-client/helpers/round'
import { featureFlag } from 'teacher-ui-client/helpers/feature-flag'
import { NoDataMessage } from 'teacher-ui-client/components/no-data-message'
import { ErrorsUnexpected } from 'teacher-ui-client/components/errors/unexpected'
import FidgetSpinnerWaveComponent from '@blakeelearning/fidget/components/fidget/spinner/wave'
import {
  serialize as serializeSort,
  deserialize as deserializeSort,
  updateSortColumns,
} from '@blakeelearning/data-tables/utils/sorting'
import { joinList } from 'teacher-ui-client/helpers/join-list'
import { isNumber } from 'lodash'

interface Signature {
  Args: {
    uiScope: ModelFor<ReportingUiScopeRoute>['uiScope']
    period: ModelFor<ReportingUiScopeRoute>['period']
  }
}

interface StudentStats {
  id: string
  first_name: string
  last_name: string
  external_id: Nullable<string>
  school_class_names: string[]
  teacher_names: string[]
  grade_position: number
  lessons_completed?: number
  total_time?: number
  skills_mastered_total?: number
  number_lessons_completed?: number
  fractions_lessons_completed?: number
  operations_algebra_lessons_completed?: number
  geometry_lessons_completed?: number
  measurement_lessons_completed?: number
  unit_quizzes_attempted?: number
  unit_quizzes_average_attempts_to_pass?: number
  unit_quizzes_average_score?: number
}

export interface ReportData extends BaseReport {
  aggregates: {
    lessons_completed: number
    total_time: number
    skills_mastered_total: number
    number_lessons_completed: number
    fractions_lessons_completed: number
    operations_algebra_lessons_completed: number
    geometry_lessons_completed: number
    measurement_lessons_completed: number
    unit_quizzes_attempted: number
  }
  students: StudentStats[]
  meta: BaseReportMetaPaging
}

// box shadow provides a way to get a border between the sticky header cells
const stickyShadowRight = 'shadow-[inset_-1px_0_0] shadow-white'

export class MathseedsPrimeDashboard extends Component<Signature> {
  @service router!: RouterService

  @service session!: SessionService

  @service reporting!: ReportingService

  @queryParam('reporting/ui-scope/mathseeds-prime/dashboard-table') pageNumber!: number

  @queryParam('reporting/ui-scope/mathseeds-prime/dashboard-table') perPage!: number

  @queryParam('reporting/ui-scope/mathseeds-prime/dashboard-table') sort!: string

  subscriptionType = SubscriptionType.Maths

  fetchReport = trackedFunction(this, () => {
    const requestScope = toRequestScope(this.args.uiScope)

    const url = this.reporting.buildGravityUrl({
      product: Product.MSP,
      requestScope,
      reportName: 'dashboard/statistics/students-table',
    })

    const query = this.reporting.buildQuery({
      sort: this.sort,
      page: {
        number: this.pageNumber,
        size: this.perPage,
      },
      period: this.args.period,
    })

    return this.reporting.fetchReport<ReportData>(url, query)
  })

  @use
  latestReport = keepLatest({
    value: () => this.fetchReport.value,
    when: () => this.fetchReport.isPending,
  })

  get pageCount() {
    return this.latestReport?.meta?.total_pages
  }

  get itemCount() {
    return this.latestReport?.meta?.total_entries
  }

  get isSubco() {
    return this.session.currentTeacher.subcoFor(this.subscriptionType)
  }

  get detailsColspan() {
    return this.isSubco ? 3 : 1
  }

  get summaryRow() {
    return this.latestReport?.aggregates
  }

  get studentRows() {
    // show empty message if no aggregates
    if (Object.values(this.latestReport?.aggregates ?? {}).every((aggregate) => !aggregate)) {
      return []
    }
    return this.latestReport?.students ?? []
  }

  @action transitionToStudentRoute(studentId: string) {
    void this.router.transitionTo('reporting.ui-scope.mathseeds-prime.dashboard-student', studentId)
  }

  <template>
    {{#if this.studentRows}}
      <div class="overflow-auto print:max-h-[unset] print:overflow-visible">
        <table
          class="table-print-zoom-1/2 relative min-w-full divide-y divide-white print:divide-black"
          data-test-dashboard-table
          exportable
        >
          <thead class="divide-y divide-white font-semibold print:divide-black">
            <tr class="bg-ms-green-150 text-center text-black">
              <th class="bg-inherit p-3 {{stickyShadowRight}}" colspan="2" {{sticky column=true z=1}}>
                {{t "student"}}
              </th>
              <div class="contents divide-x divide-white align-[inherit]">
                <th colspan={{this.detailsColspan}}></th>
                <th class="p-3" colspan="2">
                  {{t "components.reporting.tables.mathseedsPrime.dashboard.headers.programTotals"}}
                </th>
                <th class="min-w-48 p-3">
                  {{t "components.reporting.tables.mathseedsPrime.dashboard.headers.skillsMastered"}}
                </th>
                <th class="p-3" colspan={{if (featureFlag "tui_ms_prime_measurement_thread") "5" "4"}}>
                  {{t "components.reporting.tables.mathseedsPrime.dashboard.headers.lessonsCompleted"}}
                </th>
                <th class="p-3" colspan="3">
                  {{t "components.reporting.tables.mathseedsPrime.dashboard.headers.unitQuizzes"}}
                </th>
              </div>
            </tr>
            <tr class="bg-ms-green-100 text-black">
              {{! width of sticky columns MUST be locked - sticky modifier does not currently observe width changes of other cells, so does not update the offset }}
              <ThSort
                class="w-40 min-w-40 bg-inherit"
                @sortKey="first_name"
                @text={{t "reporting.dashboardTables.firstName" htmlSafe=true}}
                {{sticky column=true z=1}}
              />
              <ThSort
                class="w-40 min-w-40 bg-inherit {{stickyShadowRight}}"
                @sortKey="last_name"
                @text={{t "reporting.dashboardTables.lastName" htmlSafe=true}}
                {{sticky column=true z=1}}
              />
              <div class="contents divide-x divide-white align-[inherit]">
                <ThSort
                  class="max-w-32"
                  @sortKey="school_class_names"
                  @text={{t "reporting.dashboardTables.classes"}}
                />
                {{#if this.isSubco}}
                  <ThSort class="min-w-32" @sortKey="teacher_names" @text={{t "reporting.dashboardTables.teacher"}} />
                  <ThSort class="min-w-32" @sortKey="grade_position" @text={{t "grade.label"}} />
                {{/if}}
                <ThSort
                  class="min-w-32"
                  @sortKey="lessons_completed"
                  @text={{t "components.reporting.tables.mathseedsPrime.dashboard.headers.totalLessonsCompleted"}}
                />
                <ThSort
                  class="min-w-32"
                  @sortKey="total_time"
                  @text={{t "components.reporting.tables.mathseedsPrime.dashboard.headers.timeInProgram"}}
                />
                <ThSort
                  @sortKey="skills_mastered_total"
                  @text={{t "components.reporting.tables.mathseedsPrime.dashboard.headers.totalLessonsPassed"}}
                />
                <ThSort @sortKey="number_lessons_completed" @text={{t "mathseedsPrimeThreads.number.name"}} />
                <ThSort @sortKey="fractions_lessons_completed" @text={{t "mathseedsPrimeThreads.fractions.name"}} />
                <ThSort
                  @sortKey="operations_algebra_lessons_completed"
                  @text={{t "mathseedsPrimeThreads.operations.longName"}}
                />
                <ThSort @sortKey="geometry_lessons_completed" @text={{t "mathseedsPrimeThreads.geometry.name"}} />
                {{#if (featureFlag "tui_ms_prime_measurement_thread")}}
                  <ThSort
                    @sortKey="measurement_lessons_completed"
                    @text={{t "mathseedsPrimeThreads.measurement.name"}}
                  />
                {{/if}}
                <ThSort
                  class="min-w-32"
                  @sortKey="unit_quizzes_attempted"
                  @text={{t "components.reporting.tables.mathseedsPrime.dashboard.headers.numberOfQuizAttempts"}}
                />
                <ThSort
                  class="min-w-40"
                  @sortKey="unit_quizzes_average_attempts_to_pass"
                  @text={{t
                    "components.reporting.tables.mathseedsPrime.dashboard.headers.averageNumberOfAttemptsToPass"
                  }}
                />
                <ThSort
                  @sortKey="unit_quizzes_average_score"
                  @text={{t "components.reporting.tables.mathseedsPrime.dashboard.headers.averageQuizScore"}}
                />
              </div>
            </tr>
            <tr class="bg-ms-green-100 text-black {{if this.fetchReport.isPending 'opacity-50'}}" data-test-summary-row>
              <th class="bg-inherit p-3" colspan="2" {{sticky column=true z=1}}>
                {{t "reporting.dashboardTables.totals"}}
              </th>
              <th class="p-3" colspan={{this.detailsColspan}}></th>
              <th class="p-3">
                {{formatValue this.summaryRow.lessons_completed}}
              </th>
              <th class="p-3">
                {{formatDuration this.summaryRow.total_time "-"}}
              </th>
              <th class="p-3">
                {{formatValue this.summaryRow.skills_mastered_total}}
              </th>
              <th class="p-3">
                {{formatValue this.summaryRow.number_lessons_completed}}
              </th>
              <th class="p-3">
                {{formatValue this.summaryRow.fractions_lessons_completed}}
              </th>
              <th class="p-3">
                {{formatValue this.summaryRow.operations_algebra_lessons_completed}}
              </th>
              <th class="p-3">
                {{formatValue this.summaryRow.geometry_lessons_completed}}
              </th>
              {{#if (featureFlag "tui_ms_prime_measurement_thread")}}
                <th class="p-3">
                  {{formatValue this.summaryRow.measurement_lessons_completed}}
                </th>
              {{/if}}
              <th class="p-3">
                {{formatValue this.summaryRow.unit_quizzes_attempted}}
              </th>
              <th class="p-3" />
              <th class="p-3" />
            </tr>
          </thead>
          <tbody class="print:divide-dusty-black-100 print:divide-y {{if this.fetchReport.isPending 'opacity-50'}}">
            {{#each this.studentRows as |student index|}}
              {{! template-lint-disable no-invalid-interactive }}
              <tr
                data-test-table-row={{index}}
                class="even:bg-ms-green-50 hover:bg-ms-green-100 cursor-pointer bg-white"
                {{on "click" (fn this.transitionToStudentRoute student.id)}}
              >
                <td class="text-ms-green-300 min-w-0 bg-inherit p-3 font-bold wrap-anywhere" {{sticky column=true z=1}}>
                  {{student.first_name}}
                </td>
                <td class="text-ms-green-300 min-w-0 bg-inherit p-3 font-bold wrap-anywhere" {{sticky column=true z=1}}>
                  {{student.last_name}}
                </td>
                <td class="p-3">
                  {{joinList student.school_class_names ", "}}
                </td>
                {{#if this.isSubco}}
                  <td class="p-3">
                    {{joinList student.teacher_names ", "}}
                  </td>
                  <td class="p-3">
                    {{gradeName student.grade_position}}
                  </td>
                {{/if}}
                <td class="p-3">
                  {{formatValue student.lessons_completed}}
                </td>
                <td class="p-3">
                  {{formatDuration student.total_time "-"}}
                </td>
                <td class="p-3">
                  {{formatValue student.skills_mastered_total}}
                </td>
                <td class="p-3">
                  {{formatValue student.number_lessons_completed}}
                </td>
                <td class="p-3">
                  {{formatValue student.fractions_lessons_completed}}
                </td>
                <td class="p-3">
                  {{formatValue student.operations_algebra_lessons_completed}}
                </td>
                <td class="p-3">
                  {{formatValue student.geometry_lessons_completed}}
                </td>
                {{#if (featureFlag "tui_ms_prime_measurement_thread")}}
                  <td class="p-3">
                    {{formatValue student.measurement_lessons_completed}}
                  </td>
                {{/if}}
                <td class="p-3">
                  {{formatValue student.unit_quizzes_attempted}}
                </td>
                <td class="p-3">
                  {{formatValue
                    (if
                      (isNumber student.unit_quizzes_average_attempts_to_pass)
                      (round student.unit_quizzes_average_attempts_to_pass dp=1)
                    )
                  }}
                </td>
                <td class="p-3">
                  {{formatValue
                    (if (isNumber student.unit_quizzes_average_score) (round student.unit_quizzes_average_score))
                    (hash append="%")
                  }}
                </td>
              </tr>
            {{/each}}
          </tbody>
        </table>
      </div>
      {{#if this.pageCount}}
        <PageSelector class="mx-2 my-3" @pageCount={{this.pageCount}} @itemCount={{this.itemCount}} />
      {{/if}}
    {{else if this.fetchReport.isPending}}
      <div class="m-4" data-test-dashboard-loading><FidgetSpinnerWaveComponent @centered={{true}} /></div>
    {{else if this.fetchReport.isError}}
      <ErrorsUnexpected class="m-4" data-test-dashboard-error />
    {{else}}
      <NoDataMessage class="m-4" data-test-dashboard-empty />
    {{/if}}
  </template>
}

interface ThSortSignature {
  Element: HTMLTableCellElement
  Args: {
    text?: string
    sortKey: string
  }
}

class ThSort extends Component<ThSortSignature> {
  @queryParam('reporting/ui-scope/mathseeds-prime/dashboard-table') sort!: string

  get sortingConfig() {
    return deserializeSort(this.sort)
  }

  set sortingConfig(newSortConfig) {
    this.sort = serializeSort(newSortConfig)
  }

  @action
  updateSort() {
    this.sortingConfig = updateSortColumns(this.sortingConfig, this.args.sortKey)
  }

  get sortViewModel() {
    return getSortViewModel(this.sortingConfig, this.args.sortKey)
  }

  <template>
    <th
      class="select-none"
      data-test-sort-key={{@sortKey}}
      aria-sort={{this.sortViewModel.ariaDirection}}
      ...attributes
    >
      <button
        type="button"
        class="flex h-full w-full items-center gap-1 p-3 text-left {{if @text 'justify-between' 'justify-end'}}"
        {{on "click" this.updateSort}}
      >
        {{#if @text}}<span>{{@text}}</span>{{/if}}
        <PhosphorIcon @icon={{this.sortViewModel.icon}} class="text-dusty-black-400 print:hidden" />
      </button>
    </th>
  </template>
}
